import { useState, useEffect, useRef } from "react";
import api from "../utils/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { 
  AlertTriangle, FileText, CreditCard, Plus, ChevronDown, ChevronLeft, ChevronRight, Link2,
  Share2, Edit, Trash2, Package, ArrowRight, AlertCircle, Loader2, Check, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import CreateServiceWizard from "./CreateServiceWizard";
import ServiceSharingModal from "./ServiceSharingModal";
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import CardListViewToggle from './CardListViewToggle';
import { PrimaryButton, SecondaryButton, DangerButton } from './Buttons';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import { Badge } from "./ui/badge";

const ServicesTabImproved = ({ onNavigate }) => {
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const addDropdownRef = useRef(null);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  const [reordering, setReordering] = useState(false);
  
  // Delete modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, service: null });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Service sharing modal state
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sharingService, setSharingService] = useState(null);
  
  // Business-critical gate checks
  const [hasWaiverText, setHasWaiverText] = useState(false);
  const [hasStripeConnect, setHasStripeConnect] = useState(false);
  const [gateCheckLoading, setGateCheckLoading] = useState(true);

  useEffect(() => {
    loadServices();
    loadAssets();
    checkGateRequirements();
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(event.target)) {
        setShowAddDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkGateRequirements = async () => {
    try {
      setGateCheckLoading(true);
      const orgResponse = await api.get('/organizations/me');
      const org = orgResponse.data;
      
      const waiverText = org.customWaiverText || '';
      setHasWaiverText(waiverText.trim().length >= 100);
      setHasStripeConnect(!!(org.stripeConnectAccountId && org.stripeConnectChargesEnabled));
    } catch (error) {
      console.error("Error checking gate requirements:", error);
    } finally {
      setGateCheckLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/services');
      setServices(response.data || []);
    } catch (error) {
      console.error("Error loading services:", error);
      setError("Failed to load services");
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      setAssetsLoading(true);
      const response = await api.get('/assets');
      setAssets(response.data || []);
    } catch (error) {
      console.error("Error loading assets:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  // Move service up or down in the list
  const moveService = async (index, direction) => {
    if (reordering) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;
    
    setReordering(true);
    
    // Create new array with swapped items
    const newServices = [...services];
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];
    
    // Update local state immediately for responsiveness
    setServices(newServices);
    
    // Prepare reorder payload - update sortOrder for all services
    const reorderPayload = newServices.map((service, idx) => ({
      id: service.id,
      sortOrder: idx
    }));
    
    try {
      await api.put('/services/reorder', { services: reorderPayload });
      toast.success('Service order updated');
    } catch (error) {
      console.error('Error reordering services:', error);
      toast.error('Failed to update order');
      // Revert on failure
      loadServices();
    } finally {
      setReordering(false);
    }
  };

  const canCreateService = hasWaiverText && hasStripeConnect;

  const openServiceModal = (service = null) => {
    if (!service && !canCreateService) {
      toast.error("Complete waiver setup and Stripe Connect first");
      return;
    }
    setEditingService(service);
    setShowServiceModal(true);
  };

  const handleDeleteService = async () => {
    if (!deleteConfirm.service) return;
    
    setIsDeleting(true);
    try {
      const response = await api.delete(`/services/${deleteConfirm.service.id}`);
      const result = response.data;
      
      if (result.action === 'deactivated') {
        // Service was deactivated (soft deleted)
        if (result.activeBookings > 0) {
          toast.success(`Service deactivated. ${result.activeBookings} active booking(s) will continue until completed.`, {
            duration: 5000
          });
        } else {
          toast.success('Service deactivated. Historical bookings preserved.', {
            duration: 4000
          });
        }
        // Update local state to show as inactive
        setServices(prev => prev.map(s => 
          s.id === deleteConfirm.service.id 
            ? { ...s, isActive: false, active: false }
            : s
        ));
      } else {
        // Service was permanently deleted
        toast.success("Service permanently deleted");
        setServices(prev => prev.filter(s => s.id !== deleteConfirm.service.id));
      }
      
      setDeleteConfirm({ open: false, service: null });
    } catch (error) {
      console.error("Error deleting service:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete service";
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReactivateService = async (service) => {
    try {
      await api.post(`/services/${service.id}/reactivate`);
      toast.success("Service reactivated - toggle 'Live' when ready to show on widget");
      // Only set isActive: true to mark it as not deactivated
      // Keep active: false so user must explicitly toggle it Live
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, isActive: true, active: false } : s
      ));
    } catch (error) {
      console.error("Error reactivating service:", error);
      toast.error("Failed to reactivate service");
    }
  };

  const handlePermanentDelete = async (service) => {
    if (!confirm(`Permanently delete "${service.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/services/${service.id}/permanent`);
      toast.success("Service permanently deleted");
      setServices(prev => prev.filter(s => s.id !== service.id));
    } catch (error) {
      console.error("Error permanently deleting service:", error);
      const errorMsg = error.response?.data?.detail || "Failed to permanently delete service";
      toast.error(errorMsg);
    }
  };

  const handleToggleServiceActive = async (service, newActiveState) => {
    try {
      await api.put(`/services/${service.id}`, {
        ...service,
        active: newActiveState,
        isActive: newActiveState
      });
      
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, active: newActiveState, isActive: newActiveState } : s
      ));
      
      toast.success(newActiveState ? `${service.name} is now active` : `${service.name} is now inactive`);
    } catch (error) {
      console.error("Error toggling service:", error);
      toast.error("Failed to update service status");
      loadServices();
    }
  };

  // Error state
  if (error) {
    return (
      <TabPageLayout
        title="Services"
        subtitle="Manage bookable services"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load services</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => loadServices()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Services"
      subtitle="Manage bookable services"
      loading={loading}
      actions={
        <div className="flex items-center gap-2 w-full">
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          {/* Add Dropdown Button */}
          <div className="relative" ref={addDropdownRef}>
            <PrimaryButton 
              onClick={() => setShowAddDropdown(!showAddDropdown)} 
              className="h-9 flex-1 sm:flex-none gap-1" 
              disabled={assetsLoading || loading}
            >
              <Plus className="w-4 h-4" />
              Add
              <ChevronDown className={`w-3 h-3 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} />
            </PrimaryButton>
            
            {showAddDropdown && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => {
                    setShowAddDropdown(false);
                    if (canCreateService) {
                      openServiceModal();
                    } else {
                      toast.error("Complete waiver and Stripe setup first");
                    }
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4 text-gray-500" />
                  Create Service
                </button>
                <button
                  onClick={() => {
                    setShowAddDropdown(false);
                    onNavigate && onNavigate('partners');
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Link2 className="w-4 h-4 text-blue-600" />
                  Link Shared Service
                </button>
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Gate Warning */}
        {!gateCheckLoading && !canCreateService && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800">Complete setup to create services</h3>
                <p className="text-sm text-amber-700 mt-1 mb-3">
                  For liability protection and payment processing, complete these requirements first:
                </p>
                <div className="space-y-2">
                  {!hasWaiverText && (
                    <button
                      onClick={() => onNavigate && onNavigate('waivers')}
                      className="flex items-center gap-2 w-full p-3 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors text-left"
                    >
                      <FileText className="w-5 h-5 text-amber-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Set up your waiver</p>
                        <p className="text-xs text-gray-600">Add waiver text (100+ characters) to protect your business</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  {!hasStripeConnect && (
                    <button
                      onClick={() => onNavigate && onNavigate('payments')}
                      className="flex items-center gap-2 w-full p-3 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 transition-colors text-left"
                    >
                      <CreditCard className="w-5 h-5 text-amber-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Connect Stripe</p>
                        <p className="text-xs text-gray-600">Set up payment processing to accept bookings</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Content */}
        {services.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No Services Yet"
            description={canCreateService ? "Create your first service to start accepting bookings" : "Complete waiver and Stripe setup to create services"}
            actionLabel={canCreateService ? "Create Service" : undefined}
            onAction={canCreateService ? () => openServiceModal() : undefined}
          />
        ) : viewMode === "card" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => {
              const isDeactivated = service.isActive === false;
              
              return (
                <div 
                  key={service.id} 
                  className={`bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden transition-all relative group ${
                    isDeactivated ? 'opacity-60 ring-2 ring-red-200' : 'hover:shadow-lg'
                  }`}
                >
                  {/* Image */}
                  <div className="relative h-40 sm:h-48 bg-gray-100">
                    {service.images && service.images.length > 0 ? (
                      <img 
                        src={service.images[service.mainImageIndex || 0]} 
                        alt={service.name} 
                        className={`w-full h-full object-cover ${isDeactivated ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Reorder pill - top left of image */}
                    {services.length > 1 && (
                      <div className="absolute top-2 left-2 z-20 inline-flex items-center bg-white/95 rounded-full shadow-sm h-6 opacity-75 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); moveService(index, 'up'); }}
                          disabled={index === 0 || reordering}
                          className="px-1.5 h-6 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-l-full"
                          title="Move left"
                        >
                          <ChevronLeft className="w-3 h-3 text-gray-500" />
                        </button>
                        <span className="text-[11px] font-medium text-gray-600 px-0.5 text-center leading-none">
                          {index + 1}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); moveService(index, 'down'); }}
                          disabled={index === services.length - 1 || reordering}
                          className="px-1.5 h-6 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-r-full"
                          title="Move right"
                        >
                          <ChevronRight className="w-3 h-3 text-gray-500" />
                        </button>
                      </div>
                    )}
                    
                    {/* Deactivated Banner */}
                    {isDeactivated && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                          Deactivated
                        </span>
                      </div>
                    )}
                    
                    {/* Active Toggle - only show if not deactivated */}
                    {!isDeactivated && (
                      <div 
                        className="absolute top-2 right-2 flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 py-0.5 sm:py-1.5 rounded-full bg-white/90 backdrop-blur-sm shadow-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className={`text-[10px] sm:text-xs font-medium ${service.active ? 'text-green-600' : 'text-gray-500'}`}>
                          {service.active ? 'Live' : 'Off'}
                        </span>
                        <Switch
                          checked={service.active}
                          onCheckedChange={(checked) => handleToggleServiceActive(service, checked)}
                          className="data-[state=checked]:bg-green-500 scale-75 sm:scale-100"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1 text-gray-900">{service.name}</h3>
                    {service.tagline && (
                      <p className="text-sm text-gray-600 mb-2">{service.tagline}</p>
                    )}
                    {service.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                        {service.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="font-semibold text-blue-600">
                        {service.currency === 'AED' ? 'AED ' : '$'}{service.price || 0}
                      </span>
                      {service.duration && <span>{service.duration}min</span>}
                      {service.capacity && <span>{service.capacity} max</span>}
                    </div>
                    
                    {/* Actions - different for deactivated vs active */}
                    {isDeactivated ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleReactivateService(service)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <RefreshCw className="w-3.5 h-3.5 mr-1" />
                          Reactivate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePermanentDelete(service)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <SecondaryButton
                          size="sm"
                          onClick={() => openServiceModal(service)}
                          className="flex-1"
                        >
                          <Edit className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </SecondaryButton>
                        <SecondaryButton
                          size="sm"
                          onClick={() => {
                            setSharingService(service);
                            setShowSharingModal(true);
                          }}
                          className={service.sharing?.enabled ? 'text-green-600 border-green-300 bg-green-50' : ''}
                          title={service.sharing?.enabled ? 'Sharing enabled' : 'Configure sharing'}
                        >
                          <Share2 className="w-3.5 h-3.5" />
                        </SecondaryButton>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm({ open: true, service })}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {services.map((service) => {
                    const isDeactivated = service.isActive === false;
                    
                    return (
                      <tr key={service.id} className={`transition-colors ${isDeactivated ? 'bg-red-50/50' : 'hover:bg-gray-50'}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {service.images && service.images.length > 0 ? (
                              <img 
                                src={service.images[service.mainImageIndex || 0]} 
                                alt={service.name} 
                                className={`w-12 h-12 rounded-lg object-cover ${isDeactivated ? 'grayscale opacity-60' : ''}`}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className={`font-medium ${isDeactivated ? 'text-gray-500' : 'text-gray-900'}`}>
                                {service.name}
                                {isDeactivated && (
                                  <Badge variant="destructive" className="ml-2 text-xs">Deactivated</Badge>
                                )}
                              </div>
                              {service.tagline && (
                                <div className="text-xs text-gray-500 hidden sm:block">{service.tagline}</div>
                              )}
                              <div className="text-xs text-gray-600 sm:hidden">
                                {service.currency === 'AED' ? 'AED ' : '$'}{service.price} • {service.duration}min
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className={`font-semibold ${isDeactivated ? 'text-gray-400' : 'text-blue-600'}`}>
                            {service.currency === 'AED' ? 'AED ' : '$'}{service.price || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                          {service.duration ? `${service.duration} min` : '-'}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          {isDeactivated ? (
                            <Badge variant="outline" className="border-red-300 text-red-600">
                              Deactivated
                            </Badge>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={service.active}
                                onCheckedChange={(checked) => handleToggleServiceActive(service, checked)}
                                className="data-[state=checked]:bg-green-500"
                              />
                              <Badge variant="outline" className={service.active ? 'border-green-500 text-green-600' : 'border-gray-300 text-gray-500'}>
                                <Check className="w-3 h-3 mr-1" />
                                {service.active ? 'Live' : 'Off'}
                              </Badge>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {isDeactivated ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleReactivateService(service)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Reactivate
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePermanentDelete(service)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openServiceModal(service)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSharingService(service);
                                    setShowSharingModal(true);
                                  }}
                                  className={service.sharing?.enabled ? 'text-green-600 hover:bg-green-50' : 'text-gray-600 hover:bg-gray-100'}
                                >
                                  <Share2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteConfirm({ open: true, service })}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Service Modal */}
        <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
          <DialogContent className="max-w-xl max-h-[95vh] p-6">
            <CreateServiceWizard
              editingService={editingService}
              onComplete={(savedService) => {
                loadServices();
                setShowServiceModal(false);
                setEditingService(null);
              }}
              onCancel={() => {
                setShowServiceModal(false);
                setEditingService(null);
              }}
              assets={assets}
            />
          </DialogContent>
        </Dialog>
        
        {/* Service Sharing Modal */}
        <ServiceSharingModal
          isOpen={showSharingModal}
          onClose={() => {
            setShowSharingModal(false);
            setSharingService(null);
          }}
          service={sharingService}
          onUpdate={loadServices}
        />
        
        {/* Delete Confirmation Modal */}
        <StandardModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, service: null })}
          title="Delete Service?"
          description={`Are you sure you want to delete "${deleteConfirm.service?.name}"?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setDeleteConfirm({ open: false, service: null })}
                className="flex-1"
                disabled={isDeleting}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDeleteService}
                className="flex-1"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Service'
                )}
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>This service will no longer be bookable. Existing bookings will remain.</p>
          </InfoCard>
        </StandardModal>
      </div>
    </TabPageLayout>
  );
};

export default ServicesTabImproved;
